import React, { useCallback, useState } from "react";

import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Divider, IconButton, Popover, Stack, Typography } from "@mui/material";
import { BinDeleteIcon, OverrideIcon } from "assets/material-custom-icons";
import { RoleComponentGuard } from "components/guards";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useDealStatusesSelector } from "store";

import { Characteristic, useForm } from "..";
import { RoleName } from "../../../../constants";
import { MultiDataEditPopup } from "./MultipleDataEditPopup";

interface Props {
  characteristic: Characteristic;
  viewOnly: boolean;
  hideEyeIcon?: boolean;
}

export const MultipleData: React.FC<Props> = ({ characteristic, viewOnly, hideEyeIcon }) => {
  const {
    getManyData,
    updateChar,
    deleteChar,
    setCharacteristicRef,
    viewBy,
    overrideCharacteristicData,
    dealId,
    isOverrideAvailable,
    missingFieldNames,
    missingFieldRefsMap
  } = useForm();
  const [popupAnchor, setPopupAnchor] = useState<HTMLButtonElement | null>(null);
  const dealStatuses = useDealStatusesSelector();
  const { is_show, name, id, is_global, required_to_status_ids } = characteristic;

  const hasRestrictions = !!required_to_status_ids?.length;
  const requiredStagesTooltip = hasRestrictions
    ? `This field is required to change the deal status to: ${required_to_status_ids
        .map((statusId) => dealStatuses.find((s) => s.id === statusId)?.name ?? "")
        .filter(Boolean)
        .join(", ")}`
    : "";

  const isMissingField =
    Boolean(missingFieldNames?.length) && missingFieldNames!.some((m) => m.trim().toLowerCase() === name.toLowerCase());

  const setRef = useCallback(
    (el: HTMLDivElement | null) => {
      if (el) setCharacteristicRef(el, characteristic.id);
      if (missingFieldRefsMap && isMissingField) {
        const key = name.trim().toLowerCase();
        if (el) {
          missingFieldRefsMap.current[key] = el;
        } else {
          delete missingFieldRefsMap.current[key];
        }
      }
    },
    [setCharacteristicRef, characteristic.id, missingFieldRefsMap, name, missingFieldNames]
  );

  const data = getManyData({
    characteristic_id: id,
    investor_id: viewBy?.investor?.id || null,
    round_id: viewBy?.round?.id || null
  });

  const filteredData = data.filter((item) => !item.is_deleted);

  const checkIsOverrideAvailable = useCallback(() => {
    return isOverrideAvailable({
      dealOverrideId: !viewBy ? dealId : undefined,
      roundOverrideId: viewBy?.round?.id || undefined,
      characteristicId: id
    });
  }, [isOverrideAvailable, dealId, viewBy, id]);

  return (
    <>
      <Stack direction="row" sx={{ gap: "10px" }} ref={setRef}>
        <Stack
          sx={{
            gap: "10px",
            flexGrow: 1,
            ...(isMissingField && {
              backgroundColor: "rgba(255, 235, 59, 0.35)",
              borderRadius: 1
            })
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ position: "relative" }}>
            <Typography sx={{ fontSize: "16px", lineHeight: "24px" }}>{name}</Typography>
            {hasRestrictions && (
              <MaterialTooltip title={requiredStagesTooltip} isShow>
                <IconButton
                  size="sm"
                  sx={{
                    padding: "4px",
                    color: "lightgray",
                    marginLeft: "auto",
                    "&.MuiIconButton-root:hover": {
                      backgroundColor: "transparent",
                      color: "lightgray"
                    }
                  }}
                >
                  <HelpOutlineIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </MaterialTooltip>
            )}
          </Stack>
          <Divider sx={{ borderColor: "#DBE8F5" }} />
          <Stack sx={{ flexDirection: "row", gap: "10px", justifyContent: "space-between" }}>
            <Stack sx={{ flexDirection: "row", flexWrap: "wrap", gap: "10px", flexGrow: 1, alignItems: "flex-start" }}>
              {filteredData.length ? (
                filteredData.map((item) => (
                  <Stack
                    sx={{ flexDirection: "row", borderRadius: "20px", p: "4px", bgcolor: "#F6F8FC" }}
                    key={item.id}
                  >
                    <Typography sx={{ color: "#071F35", lineHeight: "16px", fontSize: "12px", mr: "4px" }}>
                      {item.value}
                    </Typography>
                    <Typography sx={{ fontWeight: 700, color: "#071F35", lineHeight: "16px", fontSize: "12px" }}>
                      {item.percentage}
                    </Typography>
                    <Typography sx={{ color: "#071F35", lineHeight: "16px", fontSize: "12px" }}>%</Typography>
                  </Stack>
                ))
              ) : (
                <Stack sx={{ flexDirection: "row", borderRadius: "20px", p: "4px", bgcolor: "#F6F8FC" }}>
                  <Typography sx={{ color: "#071F35", lineHeight: "16px", fontSize: "12px", mr: "4px" }}>
                    No value
                  </Typography>
                </Stack>
              )}
            </Stack>
            <IconButton disabled={viewOnly} onClick={(e) => setPopupAnchor(e.currentTarget)}>
              <EditOutlinedIcon />
            </IconButton>
          </Stack>
        </Stack>
        <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]} hideChild={hideEyeIcon}>
          <Stack direction="row" alignItems="center">
            {checkIsOverrideAvailable() && !viewOnly && !viewBy?.investor?.id && Boolean(filteredData.length) && (
              <MaterialTooltip
                title={!viewBy ? "Override all with Deal Value" : "Override all with Round Value"}
                isShow
              >
                <IconButton
                  onClick={(e) =>
                    dealId &&
                    overrideCharacteristicData({
                      dealId,
                      dealOverrideId: !viewBy ? dealId : undefined,
                      roundOverrideId: viewBy?.round?.id || undefined,
                      characteristicId: id,
                      anchorEl: e.currentTarget,
                      characteristicName: name
                    })
                  }
                >
                  <OverrideIcon />
                </IconButton>
              </MaterialTooltip>
            )}
            {!(is_global || viewOnly || Boolean(viewBy)) && (
              <IconButton onClick={() => deleteChar(characteristic)}>
                <BinDeleteIcon />
              </IconButton>
            )}
            <IconButton disabled={viewOnly || Boolean(viewBy)} onClick={() => updateChar({ id, is_show: !is_show })}>
              {is_show ? <VisibilityOutlinedIcon /> : <VisibilityOffOutlinedIcon sx={{ color: "#92A7BC" }} />}
            </IconButton>
          </Stack>
        </RoleComponentGuard>
      </Stack>
      {!viewOnly && (
        <Popover
          open={Boolean(popupAnchor)}
          anchorEl={popupAnchor}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left"
          }}
          marginThreshold={100}
          keepMounted={false}
          onClose={() => setPopupAnchor(null)}
        >
          <MultiDataEditPopup characteristic={characteristic} onClose={() => setPopupAnchor(null)} />
        </Popover>
      )}
    </>
  );
};
